import { useState, useCallback } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { cn } from "../../lib/utils";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import { Input } from "../ui/input";

export function TextFormatter() {
  const { t } = useTranslation();
  const [input, setInput] = useState("");
  const [indentSpaces, setIndentSpaces] = useState(0);
  const [trimLines, setTrimLines] = useState(false);
  const [removeEmptyLines, setRemoveEmptyLines] = useState(false);
  const [keepOneEmptyLine, setKeepOneEmptyLine] = useState(false);
  const [firstLineIndent, setFirstLineIndent] = useState(false);
  const [removeExtraSpaces, setRemoveExtraSpaces] = useState(false);
  const [addEmptyLineEveryN, setAddEmptyLineEveryN] = useState(0);
  const [copied, setCopied] = useState(false);
  const [activePreset, setActivePreset] = useState<string>("");

  const transform = useCallback(() => {
    let lines = input.split("\n");

    if (trimLines) {
      lines = lines.map(l => l.trimEnd());
    }

    if (removeExtraSpaces) {
      lines = lines.map(l => l.replace(/ {2,}/g, " "));
    }

    // 先标记段落首行，再删空行，最后缩进
    // 这样才能正确识别哪些行是段落开头

    // 1. 标记每行是否为段落首行（第一行或空行后的第一行）
    const isParagraphStart: boolean[] = [];
    let prevEmpty = true; // 第一行视为段落首行
    for (const line of lines) {
      const isEmpty = line.trim() === "";
      isParagraphStart.push(!isEmpty && prevEmpty);
      prevEmpty = isEmpty;
    }

    // 2. 删除空行
    if (removeEmptyLines) {
      if (keepOneEmptyLine) {
        // 保留段落分隔（最多1个空行）
        const result: string[] = [];
        const resultFlags: boolean[] = [];
        let lastWasContent = false;
        for (let i = 0; i < lines.length; i++) {
          if (lines[i].trim() !== "") {
            result.push(lines[i]);
            resultFlags.push(isParagraphStart[i]);
            lastWasContent = true;
          } else if (lastWasContent) {
            // 跳过空行，但在段落间保留一个标记
            // 不实际保留空行，但通过 flag 已标记下一段
            lastWasContent = false;
          }
        }
        lines = result;
        // 重新计算段落首行：第一行 + 前一行结尾不同段落的行
        // 由于空行已删除，需要用原始标记
        const newFlags: boolean[] = [true]; // 第一行总是段落首行
        for (let i = 1; i < lines.length; i++) {
          // 如果原始标记中这行是段落首行，则保持
          newFlags.push(resultFlags[i]);
        }
        // 用 newFlags 替代
        if (firstLineIndent && lines.length > 0) {
          for (let i = 0; i < lines.length; i++) {
            if (newFlags[i] && lines[i].trim() !== "") {
              lines[i] = "  " + lines[i];
            }
          }
        }
      } else {
        // 删除所有空行
        const result: string[] = [];
        const resultFlags: boolean[] = [];
        for (let i = 0; i < lines.length; i++) {
          if (lines[i].trim() !== "") {
            result.push(lines[i]);
            resultFlags.push(isParagraphStart[i]);
          }
        }
        lines = result;
        if (firstLineIndent && lines.length > 0) {
          for (let i = 0; i < lines.length; i++) {
            if (resultFlags[i] && lines[i].trim() !== "") {
              lines[i] = "  " + lines[i];
            }
          }
        }
      }
    } else {
      // 不删除空行，直接用标记缩进
      if (firstLineIndent && lines.length > 0) {
        for (let i = 0; i < lines.length; i++) {
          if (isParagraphStart[i] && lines[i].trim() !== "") {
            lines[i] = "  " + lines[i];
          }
        }
      }
    }

    // 每N行添加空行
    if (addEmptyLineEveryN > 0 && lines.length > 0) {
      const result: string[] = [];
      for (let i = 0; i < lines.length; i++) {
        result.push(lines[i]);
        // 每 N 行后加一个空行（最后一行不加）
        if ((i + 1) % addEmptyLineEveryN === 0 && i < lines.length - 1) {
          result.push("");
        }
      }
      lines = result;
    }

    if (indentSpaces > 0) {
      lines = lines.map(line => line.trim() !== "" ? " ".repeat(indentSpaces) + line : line);
    }

    return lines.join("\n");
  }, [input, indentSpaces, trimLines, removeEmptyLines, keepOneEmptyLine, firstLineIndent, removeExtraSpaces, addEmptyLineEveryN]);

  const output = transform();

  const handleCopy = async () => {
    if (!output) return;
    await navigator.clipboard.writeText(output);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleClear = () => {
    setInput("");
  };

  // 一键排版预设
  const presets = [
    {
      id: "one-click",
      icon: "✨",
      label: t("textFormatter.oneClick", "一键排版"),
      desc: t("textFormatter.oneClickDesc", "每行缩进2格+清尾空格+压缩空格+段落分隔"),
      config: { indentSpaces: 2, firstLineIndent: false, trimLines: true, removeExtraSpaces: true, removeEmptyLines: true, keepOneEmptyLine: true },
    },
    {
      id: "article",
      icon: "📝",
      label: t("textFormatter.presetArticle", "文章排版"),
      desc: t("textFormatter.presetArticleDesc", "每行缩进2格+清尾空格+段落分隔"),
      config: { indentSpaces: 2, firstLineIndent: false, trimLines: true, removeExtraSpaces: false, removeEmptyLines: true, keepOneEmptyLine: true },
    },
    {
      id: "code",
      icon: "💻",
      label: t("textFormatter.presetCode", "代码整理"),
      desc: t("textFormatter.presetCodeDesc", "2格缩进+清尾空格+压缩空格+删除空行"),
      config: { indentSpaces: 2, firstLineIndent: false, trimLines: true, removeExtraSpaces: true, removeEmptyLines: true, keepOneEmptyLine: false },
    },
    {
      id: "clean",
      icon: "🧹",
      label: t("textFormatter.presetClean", "纯清理"),
      desc: t("textFormatter.presetCleanDesc", "清尾空格+压缩空格+删除空行"),
      config: { indentSpaces: 0, firstLineIndent: false, trimLines: true, removeExtraSpaces: true, removeEmptyLines: true, keepOneEmptyLine: false },
    },
    {
      id: "reset",
      icon: "↩️",
      label: t("textFormatter.presetReset", "重置"),
      desc: t("textFormatter.presetResetDesc", "清除所有选项"),
      config: { indentSpaces: 0, firstLineIndent: false, trimLines: false, removeExtraSpaces: false, removeEmptyLines: false, keepOneEmptyLine: false },
    },
  ];

  const applyPreset = (presetId: string) => {
    const preset = presets.find(p => p.id === presetId);
    if (!preset) return;
    if (preset.id === "reset") {
      setActivePreset("");
    } else {
      setActivePreset(presetId);
    }
    setIndentSpaces(preset.config.indentSpaces);
    setFirstLineIndent(preset.config.firstLineIndent);
    setTrimLines(preset.config.trimLines);
    setRemoveExtraSpaces(preset.config.removeExtraSpaces);
    setRemoveEmptyLines(preset.config.removeEmptyLines);
    setKeepOneEmptyLine(preset.config.keepOneEmptyLine);
  };

  return (
    <div className="w-full max-w-5xl mx-auto px-4 space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="text-center space-y-2"
      >
        <div className="flex items-center justify-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-rose-500 flex items-center justify-center shadow-lg shadow-orange-500/30">
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h12M4 18h16" />
            </svg>
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-orange-500 to-rose-500 bg-clip-text text-transparent">
            {t("textFormatter.title", "文本美化排版")}
          </h1>
        </div>
        <p className="text-muted-foreground text-sm sm:text-base">
          {t("textFormatter.desc", "一键设置缩进、空行、删除空行，让文本整整齐齐")}
        </p>
      </motion.div>

      {/* Presets */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.05 }}
        className="flex flex-wrap gap-2 sm:gap-3 justify-center"
      >
        {presets.map((preset) => (
          <button
            key={preset.id}
            onClick={() => applyPreset(preset.id)}
            title={preset.desc}
            className={cn(
              "group flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 transition-all duration-200 text-sm font-medium",
              activePreset === preset.id
                ? "border-orange-500 bg-orange-50 text-orange-600 shadow-md shadow-orange-500/20"
                : "border-border/50 bg-background/60 text-foreground hover:border-orange-300 hover:bg-orange-50/50 hover:shadow-sm"
            )}
          >
            <span className="text-base">{preset.icon}</span>
            <span>{preset.label}</span>
          </button>
        ))}
      </motion.div>

      {/* Options */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.15 }}
        className="bg-card/80 backdrop-blur-sm border border-border/50 rounded-2xl p-4 sm:p-5"
      >
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4">
          {/* Indent Spaces */}
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-muted-foreground">
              {t("textFormatter.indent", "行缩进")}
            </Label>
            <Input
              type="number"
              min={0}
              max={20}
              value={indentSpaces}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setIndentSpaces(Math.max(0, parseInt(e.target.value) || 0))}
              className="h-9 text-sm"
            />
          </div>

          {/* First line indent */}
          <div className="flex items-end">
            <div className="flex items-center gap-2 h-9 px-3 border border-border/50 rounded-lg bg-background/60">
              <Checkbox
                id="firstLineIndent"
                checked={firstLineIndent}
                onCheckedChange={(v: boolean) => setFirstLineIndent(!!v)}
                className="scale-90"
              />
              <Label htmlFor="firstLineIndent" className="text-xs cursor-pointer whitespace-nowrap">
                {t("textFormatter.firstLineIndent", "首行缩进")}
              </Label>
            </div>
          </div>

          {/* Trim lines */}
          <div className="flex items-end">
            <div className="flex items-center gap-2 h-9 px-3 border border-border/50 rounded-lg bg-background/60">
              <Checkbox
                id="trimLines"
                checked={trimLines}
                onCheckedChange={(v: boolean) => setTrimLines(!!v)}
                className="scale-90"
              />
              <Label htmlFor="trimLines" className="text-xs cursor-pointer whitespace-nowrap">
                {t("textFormatter.trim", "清尾空格")}
              </Label>
            </div>
          </div>

          {/* Remove extra spaces */}
          <div className="flex items-end">
            <div className="flex items-center gap-2 h-9 px-3 border border-border/50 rounded-lg bg-background/60">
              <Checkbox
                id="removeExtraSpaces"
                checked={removeExtraSpaces}
                onCheckedChange={(v: boolean) => setRemoveExtraSpaces(!!v)}
                className="scale-90"
              />
              <Label htmlFor="removeExtraSpaces" className="text-xs cursor-pointer whitespace-nowrap">
                {t("textFormatter.removeExtraSpaces", "压缩空格")}
              </Label>
            </div>
          </div>

          {/* Remove empty lines */}
          <div className="flex items-end">
            <div className="flex items-center gap-2 h-9 px-3 border border-border/50 rounded-lg bg-background/60">
              <Checkbox
                id="removeEmptyLines"
                checked={removeEmptyLines}
                onCheckedChange={(v: boolean) => setRemoveEmptyLines(!!v)}
                className="scale-90"
              />
              <Label htmlFor="removeEmptyLines" className="text-xs cursor-pointer whitespace-nowrap">
                {t("textFormatter.removeEmpty", "删除空行")}
              </Label>
            </div>
          </div>

          {/* Add empty line every N lines */}
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-muted-foreground">
              {t("textFormatter.addEmptyEvery", "每N行加空行")}
            </Label>
            <Input
              type="number"
              min={0}
              max={50}
              value={addEmptyLineEveryN}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAddEmptyLineEveryN(Math.max(0, Math.min(50, parseInt(e.target.value) || 0)))}
              className="h-9 text-sm"
              placeholder="0=关"
            />
          </div>
        </div>

        {/* Keep one empty line option */}
        <div className={cn("mt-3 transition-all", removeEmptyLines ? "opacity-100" : "opacity-30 pointer-events-none")}>
          <div className="flex items-center gap-2 h-9 px-3 border border-border/30 rounded-lg bg-background/40">
            <Checkbox
              id="keepOneEmptyLine"
              checked={keepOneEmptyLine}
              onCheckedChange={(v: boolean) => setKeepOneEmptyLine(!!v)}
              className="scale-90"
              disabled={!removeEmptyLines}
            />
            <Label htmlFor="keepOneEmptyLine" className="text-xs cursor-pointer">
              {t("textFormatter.keepOneEmpty", "保留段落分隔（最多1个空行）")}
            </Label>
          </div>
        </div>
      </motion.div>

      {/* Text areas */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.25 }}
        className="grid grid-cols-1 lg:grid-cols-2 gap-4"
      >
        {/* Input */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="text-sm font-medium">{t("textFormatter.input", "输入文本")}</Label>
            <Button variant="ghost" size="sm" onClick={handleClear} className="h-7 text-xs text-muted-foreground hover:text-foreground">
              {t("common.clear", "清空")}
            </Button>
          </div>
          <Textarea
            value={input}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setInput(e.target.value)}
            placeholder={t("textFormatter.placeholder", "在此输入或粘贴文本...")}
            className="min-h-[300px] lg:min-h-[400px] resize-y font-mono text-sm"
          />
        </div>

        {/* Output */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="text-sm font-medium">{t("textFormatter.output", "排版结果")}</Label>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleCopy}
              disabled={!output}
              className={cn(
                "h-7 text-xs transition-all",
                copied ? "text-green-500" : "text-muted-foreground hover:text-foreground"
              )}
            >
              {copied ? (
                <span className="flex items-center gap-1">
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  {t("common.copied", "已复制")}
                </span>
              ) : (
                <span className="flex items-center gap-1">
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  {t("common.copy", "复制")}
                </span>
              )}
            </Button>
          </div>
          <Textarea
            value={output}
            readOnly
            placeholder={t("textFormatter.outputPlaceholder", "排版结果将显示在这里...")}
            className="min-h-[300px] lg:min-h-[400px] resize-y font-mono text-sm bg-muted/30"
          />
        </div>
      </motion.div>

      {/* Stats bar */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.4, delay: 0.35 }}
        className="flex flex-wrap gap-4 justify-center text-xs text-muted-foreground"
      >
        <span className="flex items-center gap-1">
          <span className="w-2 h-2 rounded-full bg-orange-400" />
          {t("textFormatter.lines", "行数")}: <strong className="text-foreground">{input ? input.split("\n").length : 0}</strong>
        </span>
        <span className="flex items-center gap-1">
          <span className="w-2 h-2 rounded-full bg-blue-400" />
          {t("textFormatter.chars", "字符")}: <strong className="text-foreground">{input.length}</strong>
        </span>
        <span className="flex items-center gap-1">
          <span className="w-2 h-2 rounded-full bg-green-400" />
          {t("textFormatter.emptyLines", "空行")}: <strong className="text-foreground">{input ? input.split("\n").filter(l => l.trim() === "").length : 0}</strong>
        </span>
      </motion.div>
    </div>
  );
}
